import { createModel } from '@rematch/core';
import { CartLineItem } from 'domains/cartDomain';
import { RootModel } from 'models';
import jobEstimatorService from 'services/jobEstimatorService';
import {
  AdjustMarkupPayload,
  AdjustPressPayload,
  ArtworkFileParams,
  ChangeOrgPayload,
  JobEstimator,
  JobEstimatorComparatorItem,
  JobEstimatorConfirmPaperUpdateParamsColumnType,
  JobRFQAdjustPayload,
  JobRFQOrgItemViewer,
  JobRFQResponseViewer,
  JobRFQViewer,
  JobSpec,
} from '../domains/jobconfigDomain';
import { baseReducers, BaseState } from './baseReducers';

interface JobEstimatorState extends BaseState {
  choiceSerialNumber: number;
  currentComparablePaper: {
    type: JobEstimatorConfirmPaperUpdateParamsColumnType;
    data: JobSpec;
  };
}

export const jobEstimator = createModel<RootModel>()({
  state: {
    choiceSerialNumber: 0,
    currentComparablePaper: {
      type: JobEstimatorConfirmPaperUpdateParamsColumnType.CURRENT,
      data: {},
    },
  } as JobEstimatorState,
  reducers: {
    ...baseReducers<JobEstimatorState>(),
  },
  effects: (dispatch) => {
    // const { jobEstimator } = dispatch;
    return {
      async changeOrg(
        payload: ChangeOrgPayload
      ): Promise<JobEstimatorComparatorItem> {
        return await jobEstimatorService.changeOrg(payload);
      },
      async adjustPress(
        payload: AdjustPressPayload
      ): Promise<JobEstimatorComparatorItem> {
        return await jobEstimatorService.adjustPress(payload);
      },
      async adjustMarkup(
        payload: AdjustMarkupPayload
      ): Promise<JobEstimatorComparatorItem> {
        return await jobEstimatorService.adjustMarkup(payload);
      },
      async adjustParcel(
        payload: AdjustMarkupPayload
      ): Promise<JobEstimatorComparatorItem> {
        return await jobEstimatorService.adjustParcel(payload);
      },
      async changeShipping(
        payload: Partial<JobRFQAdjustPayload>
      ): Promise<JobEstimatorComparatorItem> {
        return await jobEstimatorService.changeShipping(payload);
      },
      async getJobEstimator(payload: {
        jobConfigId: string;
      }): Promise<JobEstimator> {
        return await jobEstimatorService.getJobEstimator(
          payload.jobConfigId || ''
        );
      },
      async generate(payload: {
        jobConfigId: string;
        quantityItemSerialNumber: number;
      }): Promise<JobEstimator> {
        return await jobEstimatorService.generate(payload);
      },
      async addToCart(payload: {
        jobConfigId: string;
        quantityItemSerialNumber: number;
        comparatorItemSerialNumber: number;
      }): Promise<CartLineItem> {
        return await jobEstimatorService.addToCart(payload);
      },
      async findAvlOrgs(payload: {
        jobConfigId: string;
        quantityItemSerialNumber: number;
      }): Promise<any> {
        return await jobEstimatorService.findAvlOrgs(payload);
      },
      async init(payload: {
        jobConfigId: string;
        isProxied: boolean;
      }): Promise<JobRFQViewer> {
        return await jobEstimatorService.init(payload);
      },
      async getRfqSupplierList(): Promise<JobRFQOrgItemViewer[]> {
        return await jobEstimatorService.getRfqSupplierList();
      },
      async markAsRead(id: number | string): Promise<boolean> {
        return await jobEstimatorService.markAsRead(id);
      },
      async rfqDecline(orgItemId: number): Promise<JobRFQOrgItemViewer[]> {
        return await jobEstimatorService.rfqDecline(orgItemId);
      },
      async rfqView(payload: { orgItemId: number; filterLatestRevision?: boolean }): Promise<JobRFQResponseViewer> {
        return await jobEstimatorService.rfqView(payload.orgItemId, payload.filterLatestRevision ?? true);
      },
      async rfqSend(payload: {
        orgItemId: number;
        serialNumbers: number[];
      }): Promise<any> {
        return await jobEstimatorService.rfqSend(payload);
      },
      async rfqResponseUpdate(payload: {
        orgItemId: number;
        serialNumbers: number[];
      }): Promise<any> {
        return await jobEstimatorService.rfqResponseUpdate(payload);
      },
      async rfqAdjust(
        payload: Partial<JobRFQAdjustPayload>
      ): Promise<JobRFQResponseViewer> {
        return await jobEstimatorService.rfqAdjust(payload);
      },
      async rfqAdjustNote(
        payload: Partial<JobRFQAdjustPayload>
      ): Promise<JobRFQResponseViewer> {
        return await jobEstimatorService.rfqAdjustNote(payload);
      },
      async getRfqCache(payload: number): Promise<any> {
        return await jobEstimatorService.getRfqCache(payload);
      },
      async getRfqFilesList(payload: number): Promise<any> {
        return await jobEstimatorService.getRfqFilesList(payload);
      },
      async getRfqSupplierFilesList(payload: number): Promise<any> {
        return await jobEstimatorService.getRfqSupplierFilesList(payload);
      },
      async deleteRfqSupplierFile(payload: {
        rfqId: number;
        id: string;
      }): Promise<any> {
        return await jobEstimatorService.deleteRfqSupplierFile(payload);
      },
      async uploadRfqSupplierFile(payload: ArtworkFileParams): Promise<any> {
        return await jobEstimatorService.uploadRfqSupplierFile(payload);
      },
      async rfqSupplierAdjust(payload: {
        id: number;
        orgId: number;
        qtySerialNumber: number;
      }): Promise<any> {
        return await jobEstimatorService.rfqSupplierAdjust(payload);
      },
    };
  },
});
